import { useGT } from 'gt-react';
import { useUploadEntityDocument } from '@/api/rpc/hooks/core/useEntityDocuments';
import {
  useFileArCollectionThreadDocumentEvidence,
  useFileArCollectionThreadEmailFileEvidence,
} from '@/api/rpc/hooks/accounts-receivable/useArCollectionThreads';
import { useToast } from 'src/context/ToastContext';
import { validateFilesForUpload } from 'src/lib/file-upload-validation';
import { useUploadProgressRows } from '@/hooks/useUploadProgressRows';

/** entityType the upload flow tags collection-thread documents with. */
const COLLECTION_THREAD_ENTITY_TYPE = 'ar_collection_thread';

/**
 * Files routed through the email-evidence path instead of the document path.
 * Extension check only — the server re-sniffs the content by magic bytes and
 * rejects files that aren't really emails.
 */
const EMAIL_FILE_EXTENSIONS = ['.eml', '.msg'];

function isEmailFile(fileName: string): boolean {
  const lower = fileName.toLowerCase();
  return EMAIL_FILE_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

export interface UploadThreadEvidenceOutcome {
  /** Files uploaded and filed as evidence. */
  succeeded: number;
  /** Files whose upload or filing failed (their progress rows carry the error). */
  failed: number;
}

/**
 * The collection-thread evidence upload pipeline, shared by the Upload dialog
 * and the always-on page dropzone so the two paths cannot drift. Two steps per
 * file: presigned upload to storage (useUploadEntityDocument, which also
 * toasts per-file success/failure and confirms the upload), then
 * file-as-evidence (links it into the thread's evidence graph + posts a
 * timeline activity, invalidating the thread + evidence queries via the
 * mutation hooks). `.eml`/`.msg` files take the email path instead — the
 * server parses them into a real inbound email so they render in the email
 * viewer rather than as an opaque blob; an already-filed email is a benign
 * no-op surfaced as an info toast. Mixed batches are fine: each file picks its
 * own path.
 *
 * `uploadFiles` applies the same validation FileUploadDropzone enforces
 * (batch-size cap rejects the whole batch, oversized files are rejected
 * individually), surfacing rejections as error toasts — callers behind the
 * dropzone only ever hand it pre-validated files, so those toasts never
 * double up with the dropzone's inline alerts.
 */
export function useUploadThreadEvidence(threadId: string) {
  const gt = useGT();
  const { showToast } = useToast();
  const { progress, track, reset } = useUploadProgressRows();
  const upload = useUploadEntityDocument();
  const fileEvidence = useFileArCollectionThreadDocumentEvidence();
  const fileEmailEvidence = useFileArCollectionThreadEmailFileEvidence();

  const isUploading =
    upload.isPending || fileEvidence.isPending || fileEmailEvidence.isPending;

  async function fileOne(fileName: string, documentId: string): Promise<void> {
    if (!isEmailFile(fileName)) {
      await fileEvidence.mutateAsync({ id: threadId, documentId });
      return;
    }
    // Email files become a real inbound email (parsed server-side) rather
    // than an opaque document. An already-filed email is a benign no-op —
    // tell the rep rather than erroring the row.
    const result = await fileEmailEvidence.mutateAsync({
      id: threadId,
      documentId,
    });
    if (!result.filed) {
      showToast({
        color: 'info',
        title: gt('Already on this thread', {
          context: 'toast title, uploaded email was already filed as evidence',
        }),
        message: gt('This email is already filed as evidence here.', {
          context:
            'toast message, uploaded email was already filed as evidence',
        }),
      });
    }
  }

  /** Uploads + files one file, reporting into `progress`. Never throws. */
  async function uploadOne(file: File): Promise<boolean> {
    const documentId = await track(file, async (setPercent) => {
      const result = await upload.mutateAsync({
        entityType: COLLECTION_THREAD_ENTITY_TYPE,
        entityId: threadId,
        file,
      });
      // The upload is a single presigned PUT with no progress events, so the
      // bar moves on real milestones: stored + confirmed (90), then filed.
      setPercent(90);
      await fileOne(file.name, result.documentId);
      return result.documentId;
    });
    return documentId !== null;
  }

  /**
   * Validate, upload and file a batch. Resolves once every file has settled;
   * per-file progress accumulates in `progress` as it goes.
   */
  async function uploadFiles(
    files: File[],
  ): Promise<UploadThreadEvidenceOutcome> {
    const { validFiles, errors } = validateFilesForUpload(files);
    for (const rejection of errors) {
      showToast({
        color: 'error',
        title: gt('Upload failed', {
          context: 'toast title, a dropped file was rejected before upload',
        }),
        message: `${rejection.file.name}: ${rejection.error}`,
      });
    }
    const results = await Promise.all(
      validFiles.map((file) => uploadOne(file)),
    );
    const succeeded = results.filter(Boolean).length;
    return { succeeded, failed: results.length - succeeded };
  }

  return { uploadFiles, progress, isUploading, resetProgress: reset };
}
